/**
 * @element qds-toast
 *
 * A component for displaying temporary notification messages that overlay the UI
 * and automatically disappear after a set duration.
 *
 * @attr {string} type - The type of toast message: 'info' (default), 'success', 'warning', 'error'
 * @attr {string} message - The message text to display in the toast
 * @attr {number} duration - Duration in milliseconds before the toast auto-dismisses (default: 3000)
 * @attr {string} position - Position on screen: 'top-right' (default), 'top-left', 'bottom-right', 'bottom-left', 'top-center', 'bottom-center', 'cursor'
 * @attr {number} cursor-x - X position of cursor when using 'cursor' position (automatically set)
 * @attr {number} cursor-y - Y position of cursor when using 'cursor' position (automatically set)
 * @attr {number} cursor-offset - Offset in pixels from cursor position (default: 10)
 *
 * @csspart container - The toast container element
 * @csspart icon - The icon element
 * @csspart message - The text message element
 * @csspart close-button - The close button element
 *
 * @fires qds-toast-show - When the toast is shown
 * @fires qds-toast-hide - When the toast is hidden
 */

import { ToastType, ToastPosition, ToastOptions, QdsToastManager, ToastEventDetail } from '../../types/index.js';
import { loadBaseCSS } from '../shared/button-utils.js';

declare global {
  interface Window {
    QdsToastManager?: QdsToastManager;
  }
}

class QdsToast extends HTMLElement {
  // Private properties
  private _type: ToastType;
  private _message: string;
  private _duration: number;
  private _position: ToastPosition;
  private _cursorX: number;
  private _cursorY: number;
  private _cursorOffset: number;
  private _visible: boolean;
  private _timeout: number | null;
  private _boundHandleCloseClick: (e: MouseEvent) => void;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    // Initialize default property values
    this._type = 'info';
    this._message = '';
    this._duration = 3000;
    this._position = 'top-right';
    this._cursorX = 0;
    this._cursorY = 0;
    this._cursorOffset = 10;
    this._visible = false;
    this._timeout = null;
    
    // Ensure CSS is loaded
    this._ensureBaseStylesLoaded();
    
    // Set up initial DOM structure
    this.render();
    
    // Bind methods
    this._boundHandleCloseClick = this._handleCloseClick.bind(this);
    
    // Create singleton manager if it doesn't exist yet
    if (!window.QdsToastManager) {
      window.QdsToastManager = this._createToastManager();
    }
  }
  
  // Define observed attributes
  static get observedAttributes(): string[] {
    return ['type', 'message', 'duration', 'position', 'cursor-x', 'cursor-y', 'cursor-offset'];
  }
  
  // Handle attribute changes
  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;
    
    switch (name) {
      case 'type':
        this._type = (newValue || 'info') as ToastType;
        this._updateType();
        break;
      case 'message':
        this._message = newValue || '';
        this._updateMessage();
        break;
      case 'duration':
        this._duration = parseInt(newValue, 10) || 3000;
        break;
      case 'position':
        this._position = (newValue || 'top-right') as ToastPosition;
        this._updatePosition();
        break;
      case 'cursor-x':
        this._cursorX = parseInt(newValue, 10) || 0;
        if (this._position === 'cursor') {
          this._updatePosition();
        }
        break;
      case 'cursor-y':
        this._cursorY = parseInt(newValue, 10) || 0;
        if (this._position === 'cursor') {
          this._updatePosition();
        }
        break;
      case 'cursor-offset':
        this._cursorOffset = parseInt(newValue, 10) || 10;
        if (this._position === 'cursor') {
          this._updatePosition();
        }
        break;
    }
  }
  
  /**
   * Ensures that the base CSS styles are loaded in the document
   * @private
   */
  private _ensureBaseStylesLoaded(): void {
    loadBaseCSS();
  }
  
  /**
   * Update the toast type styling
   * @private
   */
  private _updateType(): void {
    if (!this.shadowRoot) return;
    
    const container = this.shadowRoot.querySelector('.toast-container') as HTMLElement | null;
    if (container) {
      // Remove existing type classes
      container.classList.remove('toast-info', 'toast-success', 'toast-warning', 'toast-error');
      // Add the new type class
      container.classList.add(`toast-${this._type}`);
    }
    
    this._updateIcon();
  }
  
  /**
   * Update the toast icon based on type
   * @private
   */
  private _updateIcon(): void {
    if (!this.shadowRoot) return;
    
    const iconElement = this.shadowRoot.querySelector('.toast-icon') as HTMLElement | null;
    if (!iconElement) return;
    
    // Choose icon based on type
    let iconSvg = '';
    switch (this._type) {
      case 'success':
        iconSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
          <path fill="none" d="M0 0h24v24H0z"/>
          <path fill="currentColor" d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm-.997-4L6.76 11.757l1.414-1.414 2.829 2.829 5.656-5.657 1.415 1.414L11.003 16z"/>
        </svg>`;
        break;
      case 'warning':
        iconSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
          <path fill="none" d="M0 0h24v24H0z"/>
          <path fill="currentColor" d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm-1-5h2v2h-2v-2zm0-8h2v6h-2V7z"/>
        </svg>`;
        break;
      case 'error':
        iconSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
          <path fill="none" d="M0 0h24v24H0z"/>
          <path fill="currentColor" d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm0-9.414l2.828-2.829 1.415 1.415L13.414 12l2.829 2.828-1.415 1.415L12 13.414l-2.828 2.829-1.415-1.415L10.586 12 7.757 9.172l1.415-1.415L12 10.586z"/>
        </svg>`;
        break;
      case 'info':
      default:
        iconSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
          <path fill="none" d="M0 0h24v24H0z"/>
          <path fill="currentColor" d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM11 7h2v2h-2V7zm0 4h2v6h-2v-6z"/>
        </svg>`;
        break;
    }
    
    iconElement.innerHTML = iconSvg;
  }
  
  /**
   * Update the toast message text
   * @private
   */
  private _updateMessage(): void {
    if (!this.shadowRoot) return;
    
    const messageElement = this.shadowRoot.querySelector('.toast-message') as HTMLElement | null;
    if (messageElement) {
      messageElement.textContent = this._message;
    }
  }
  
  /**
   * Update the toast position styling
   * @private
   */
  private _updatePosition(): void {
    if (!this.shadowRoot) return;
    
    const container = this.shadowRoot.querySelector('.toast-container') as HTMLElement | null;
    if (!container) return;
    
    // Remove existing position classes
    container.classList.remove(
      'position-top-right',
      'position-top-left',
      'position-bottom-right',
      'position-bottom-left',
      'position-top-center',
      'position-bottom-center',
      'position-cursor'
    );
    
    // Reset any inline position styles
    container.style.top = '';
    container.style.left = '';
    container.style.right = '';
    container.style.bottom = '';
    container.style.transform = '';
    
    if (this._position === 'cursor') {
      // For cursor position, set specific coordinates
      container.classList.add('position-cursor');
      
      // Adjust position based on cursor coordinates
      const offset = this._cursorOffset;
      
      // Get viewport dimensions
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Get toast dimensions
      const rect = container.getBoundingClientRect();
      const toastWidth = rect.width || 300;  // Fallback width if not available
      const toastHeight = rect.height || 80; // Fallback height if not available
      
      // Calculate position - make toast appear near cursor but avoid overflow
      let left = this._cursorX + offset;
      let top = this._cursorY + offset;
      
      // Make sure toast stays within viewport
      if (left + toastWidth > viewportWidth) {
        left = this._cursorX - toastWidth - offset;
      }
      
      if (top + toastHeight > viewportHeight) {
        top = this._cursorY - toastHeight - offset;
      }
      
      // Set position with safe values
      container.style.left = `${Math.max(0, left)}px`;
      container.style.top = `${Math.max(0, top)}px`;
    } else {
      // For standard positions, add the class
      container.classList.add(`position-${this._position}`);
    }
  }
  
  /**
   * Handle close button click
   * @private
   */
  private _handleCloseClick(event: MouseEvent): void {
    this.hide();
  }
  
  /**
   * Create the toast manager singleton
   * @private
   * @returns The toast manager interface
   */
  private _createToastManager(): QdsToastManager {
    // Create local show function to avoid repeated reference to window.QdsToastManager
    const localShow = (message: string, options: ToastOptions = {}): HTMLElement => {
      // Create a toast element if it doesn't exist
      let toast = document.querySelector('qds-toast') as HTMLElement | null;
      if (!toast) {
        toast = document.createElement('qds-toast') as HTMLElement;
        document.body.appendChild(toast);
      }
      
      // Configure the toast
      toast.setAttribute('message', message);
      if (options.type) toast.setAttribute('type', options.type);
      if (options.duration) toast.setAttribute('duration', options.duration.toString());
      if (options.position) toast.setAttribute('position', options.position);
      
      // Handle cursor position
      if (options.position === 'cursor' && options.event) {
        toast.setAttribute('cursor-x', options.event.clientX.toString());
        toast.setAttribute('cursor-y', options.event.clientY.toString());
        if (options.cursorOffset) {
          toast.setAttribute('cursor-offset', options.cursorOffset.toString());
        }
      }
      
      // Show the toast
      (toast as any).show();
      return toast;
    };
    
    return {
      /**
       * Show a toast message
       * @param message - The message to display
       * @param options - Configuration options
       * @returns The toast element
       */
      show: localShow,
      
      /**
       * Success toast shorthand
       * @param message - The message to display
       * @param options - Configuration options
       */
      success: (message: string, options: Omit<ToastOptions, 'type'> = {}): HTMLElement => {
        return localShow(message, { ...options, type: 'success' });
      },
      
      /**
       * Error toast shorthand
       * @param message - The message to display
       * @param options - Configuration options
       */
      error: (message: string, options: Omit<ToastOptions, 'type'> = {}): HTMLElement => {
        return localShow(message, { ...options, type: 'error' });
      },
      
      /**
       * Warning toast shorthand
       * @param message - The message to display
       * @param options - Configuration options
       */
      warning: (message: string, options: Omit<ToastOptions, 'type'> = {}): HTMLElement => {
        return localShow(message, { ...options, type: 'warning' });
      },
      
      /**
       * Info toast shorthand
       * @param message - The message to display
       * @param options - Configuration options
       */
      info: (message: string, options: Omit<ToastOptions, 'type'> = {}): HTMLElement => {
        return localShow(message, { ...options, type: 'info' });
      }
    };
  }
  
  /**
   * Show the toast
   * @public
   */
  public show(): void {
    if (this._visible) return;
    
    // Clear any existing timeout
    if (this._timeout) {
      clearTimeout(this._timeout);
      this._timeout = null;
    }
    
    // Make the toast visible
    const container = this.shadowRoot?.querySelector('.toast-container') as HTMLElement | null;
    if (container) {
      this._visible = true;
      container.classList.add('visible');
      
      // Dispatch show event
      this.dispatchEvent(new CustomEvent<ToastEventDetail>('qds-toast-show', {
        bubbles: true,
        composed: true,
        detail: {
          type: this._type,
          message: this._message
        }
      }));
      
      // Set auto-dismiss timeout
      this._timeout = window.setTimeout(() => {
        this.hide();
      }, this._duration);
    }
  }
  
  /**
   * Hide the toast
   * @public
   */
  public hide(): void {
    if (!this._visible) return;
    
    // Clear any existing timeout
    if (this._timeout) {
      clearTimeout(this._timeout);
      this._timeout = null;
    }
    
    // Hide the toast
    const container = this.shadowRoot?.querySelector('.toast-container') as HTMLElement | null;
    if (container) {
      this._visible = false;
      container.classList.remove('visible');
      
      // Dispatch hide event
      this.dispatchEvent(new CustomEvent<ToastEventDetail>('qds-toast-hide', {
        bubbles: true,
        composed: true,
        detail: {
          type: this._type,
          message: this._message
        }
      }));
    }
  }
  
  /**
   * Render the toast element
   * @private
   */
  private render(): void {
    if (!this.shadowRoot) return;
    
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          --toast-info-color: var(--theme-info);
          --toast-success-color: var(--theme-success);
          --toast-warning-color: var(--theme-warning);
          --toast-error-color: var(--theme-error);
          --toast-text-color: var(--theme-text-primary);
          --toast-background: var(--theme-background-elevated, var(--theme-background-primary));
          --toast-shadow: var(--theme-shadow-lg);
          
          position: fixed;
          z-index: 9999;
          pointer-events: none;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
          overflow: visible;
          display: flex;
        }
        
        .toast-container {
          position: fixed;
          display: flex;
          align-items: center;
          min-width: 250px;
          max-width: 450px;
          min-height: 48px;
          background-color: var(--toast-background);
          color: var(--toast-text-color);
          border-radius: 6px;
          padding: 12px 16px;
          box-shadow: var(--toast-shadow);
          transition: transform 0.3s ease-out, opacity 0.3s ease-out;
          pointer-events: auto;
          opacity: 0;
          transform: translateY(-20px);
          z-index: 9999;
          border-left: 4px solid transparent;
          border: 1px solid var(--theme-border-primary);
        }
        
        /* Position classes */
        .position-top-right {
          top: 20px;
          right: 20px;
        }
        
        .position-top-left {
          top: 20px;
          left: 20px;
        }
        
        .position-bottom-right {
          bottom: 20px;
          right: 20px;
          transform: translateY(20px);
        }
        
        .position-bottom-left {
          bottom: 20px;
          left: 20px;
          transform: translateY(20px);
        }
        
        .position-top-center {
          top: 20px;
          left: 50%;
          transform: translateX(-50%) translateY(-20px);
        }
        
        .position-bottom-center {
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%) translateY(20px);
        }
        
        /* Cursor position - positioned via JavaScript */
        .position-cursor {
          position: fixed;
          /* top and left are set inline via JavaScript */
          transform: none;
          transition: opacity 0.3s ease-out;
        }
        
        /* Visible state */
        .visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        .position-top-center.visible, .position-bottom-center.visible {
          transform: translateX(-50%) translateY(0);
        }
        
        /* Type-specific styles */
        .toast-info {
          border-left-color: var(--toast-info-color);
        }
        
        .toast-success {
          border-left-color: var(--toast-success-color);
        }
        
        .toast-warning {
          border-left-color: var(--toast-warning-color);
        }
        
        .toast-error {
          border-left-color: var(--toast-error-color);
        }
        
        /* Content layout */
        .toast-icon {
          flex-shrink: 0;
          margin-right: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .toast-icon svg {
          width: 24px;
          height: 24px;
        }
        
        .toast-info .toast-icon {
          color: var(--toast-info-color);
        }
        
        .toast-success .toast-icon {
          color: var(--toast-success-color);
        }
        
        .toast-warning .toast-icon {
          color: var(--toast-warning-color);
        }
        
        .toast-error .toast-icon {
          color: var(--toast-error-color);
        }
        
        .toast-message {
          flex-grow: 1;
          font-size: 14px;
          line-height: 1.4;
          margin: 0 12px 0 0;
        }
        
        .toast-close {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          background: transparent;
          padding: 4px;
          cursor: pointer;
          color: var(--theme-text-secondary);
          border-radius: 50%;
          width: 24px;
          height: 24px;
          transition: background-color 0.2s;
        }
        
        .toast-close:hover {
          background-color: var(--button-hover-bg, var(--theme-background-hover));
        }
        
        .toast-close:focus {
          outline: none;
          box-shadow: 0 0 0 2px var(--button-focus-ring);
        }
        
        .toast-close svg {
          width: 16px;
          height: 16px;
        }
        
        @media (max-width: 576px) {
          .toast-container {
            min-width: auto;
            max-width: calc(100% - 40px);
            width: calc(100% - 40px);
          }
          
          .position-top-center, .position-bottom-center,
          .position-top-left, .position-top-right,
          .position-bottom-left, .position-bottom-right {
            left: 20px;
            right: 20px;
            transform: translateY(-20px);
          }
          
          .position-bottom-center, .position-bottom-left, .position-bottom-right {
            transform: translateY(20px);
          }
          
          .position-top-center.visible, .position-bottom-center.visible,
          .position-top-left.visible, .position-top-right.visible,
          .position-bottom-left.visible, .position-bottom-right.visible {
            transform: translateY(0);
          }
        }
      </style>
      
      <div class="toast-container toast-${this._type} position-${this._position}" part="container" role="alert" aria-live="polite">
        <div class="toast-icon" part="icon">
          <!-- Icon will be injected via JavaScript -->
        </div>
        <div class="toast-message" part="message">${this._message}</div>
        <button class="toast-close" part="close-button" aria-label="Close">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
            <path fill="none" d="M0 0h24v24H0z"/>
            <path fill="currentColor" d="M12 10.586l4.95-4.95 1.414 1.414-4.95 4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95 4.95-1.414-1.414 4.95-4.95-4.95-4.95L7.05 5.636z"/>
          </svg>
        </button>
      </div>
    `;
    
    // Update visual elements
    this._updateType();
    this._updateMessage();
    this._updatePosition();
    
    // Add event listeners
    const closeButton = this.shadowRoot.querySelector('.toast-close') as HTMLElement;
    if (closeButton) {
      closeButton.addEventListener('click', this._boundHandleCloseClick);
    }
  }
  
  // Clean up event listeners when element is removed
  disconnectedCallback(): void {
    const closeButton = this.shadowRoot?.querySelector('.toast-close') as HTMLElement | null;
    if (closeButton) {
      closeButton.removeEventListener('click', this._boundHandleCloseClick);
    }
    
    // Clear any existing timeout
    if (this._timeout) {
      clearTimeout(this._timeout);
      this._timeout = null;
    }
  }
}

// Only define the custom element if it's not already defined
if (!customElements.get('qds-toast')) {
  customElements.define('qds-toast', QdsToast);
}

export { QdsToast };
